import { Image } from "@insite/content-library/Widgets/Basic/Image";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    itemFields: any;
    onShowNextItem: () => void;
    onShowPreviousItem: () => void;
    activeItem: number;
    onSelectItem: (index: number) => void;
    totalItems: number;
    bulletColor: string;
    activeBulletColor: string;
    arrowColor: string;
}

export interface ContentBoxStyles {
    titleGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    contentGridContainer: GridContainerProps;
    contentGridItem?: GridItemProps;
    imagesGridItem?: GridItemProps;
    subtitleLabel?: TypographyPresentationProps;
    link1Label?: InjectableCss;
    link2Label?: InjectableCss;
    iconArrow?: InjectableCss;
    bulletsGridContainer?: GridContainerProps;
    bulletsGridItem?: GridItemProps;
}

export const contentBoxStyles: ContentBoxStyles = {
    titleGridContainer: {
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    titleGridItem: {
        width: 100 as GridWidths,
        css: css`
            justify-content: space-around;
            align-items: center;
        `,
    },
    titleLabel: {},
    contentGridContainer: {
        css: css`
            width: 100%;
        `,
        offsetCss: css`
            flex-wrap: nowrap !important;
            align-items: center;
        `,
    },
    contentGridItem: {
        width: 50 as GridWidths,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    imagesGridItem: {
        width: 50 as GridWidths,
        css: css`
            padding: 20px;
        `,
    },
    iconArrow: {
        css: css`
            margin-left: 30px;
            margin-right: 30px;
        `,
    },
    subtitleLabel: {
        css: css`
            margin: 30px;
        `,
    },
    link1Label: {
        css: css`
            margin: 30px;
        `,
    },
    link2Label: {
        css: css`
            margin: 30px;
        `,
    },
    bulletsGridContainer: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
        `,
    },
    bulletsGridItem: {
        width: 100 as GridWidths,
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },
};

export const bulletPointsStyles = {
    bullet: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        margin: '5px',
        display: 'inline-block',
        position: 'relative', 
        cursor: 'pointer',
        backgroundColor: '#0072BC',
    },
    activeBullet: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        backgroundColor: 'white',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
    }
};

export const ContentBoxItem = ({ itemFields, onShowNextItem, onShowPreviousItem, activeItem, onSelectItem, totalItems, bulletColor, activeBulletColor, arrowColor}: OwnProps) => {
    const styles = contentBoxStyles;

    if (!itemFields) {
        return null;
    }
    
    const item = itemFields.fields;

    if (!item) {
        return null;
    }

    const imageStyle = {
        transform: `scale(${itemFields.imageZoom / 100})`
    };

    const subtitleStyle = {
        ...styles.subtitleLabel,
        margin: `${itemFields.subtitleMargin}px`,
    };

    const linkStyles = {
        ...styles.link1Label,
        margin: `${itemFields.linksMargin}px`,
    };

    const computeBulletStyles = () => {
        return {
            bullet: {
                ...bulletPointsStyles.bullet,
                backgroundColor: bulletColor,
            },
            activeBullet: {
                ...bulletPointsStyles.activeBullet,
                backgroundColor: activeBulletColor,
            },
        };
    };

    const bulletPointStyles = computeBulletStyles();

    const computeArrowStyles = () => {
        return {
            ...styles.iconArrow,
            color: arrowColor,
            cursor: "pointer"
        };
    };

    const arrowStyles = computeArrowStyles();

    const renderBullets = () => {
        const bullets = [];
        for (let i = 0; i < totalItems; i++) {
          bullets.push(
            <div 
              key={i}
              onClick={() => onSelectItem(i)}
              style={bulletPointStyles.bullet as React.CSSProperties}
            >
              {i + 1 === activeItem && (
                <div style={bulletPointStyles.activeBullet as React.CSSProperties} />
              )}
            </div>
          );
        }
        return bullets;
    };    

    return (
        <>
            <GridContainer {...styles.titleGridContainer}>
                <GridItem {...styles.titleGridItem}>
                    <Icon src="ArrowLeft" size={25} style={arrowStyles} onClick={onShowPreviousItem} />
                    <Typography {...styles.titleLabel}>{parse(item.title)}</Typography>
                    <Icon src="ArrowRight" size={25} style={arrowStyles} onClick={onShowNextItem} />
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.contentGridContainer}>
                <GridItem {...styles.contentGridItem}>
                    <Typography style={subtitleStyle}>{parse(item.subtitle)}</Typography>{" "}
                    <Link style={linkStyles} href={item.button1Link} target="true">
                        <Button variant="primary">{parse(item.button1Text)}</Button>
                    </Link>
                    <Link style={linkStyles} href={item.button2Link} target="true">
                        <Button variant="primary">{parse(item.button2Text)}</Button>
                    </Link>
                </GridItem>
                <GridItem {...styles.imagesGridItem}>
                    <img src={item.image} alt="Missing" style={imageStyle}/>
                </GridItem>
            </GridContainer>
            <GridContainer {...styles.bulletsGridContainer}>
                <GridItem {...styles.bulletsGridItem}>
                    {renderBullets()}
                </GridItem>
            </GridContainer>
        </>
    );
};

export default ContentBoxItem;
